import { useDayPeriodStore } from '../../store/useDayPeriodStore'
import { Spinner } from '@heroui/react'
import { StationPanelLinePhoto } from '../panels/StationPanelLinePhoto'
import { DayPeriodPicker } from './shared/DayPeriodPicker'
import { MinGrid } from './shared/MinGrid'
import { TimeZonePicker } from './shared/TimeZonePicker'
import { TableOrientationToggle } from './shared/TableOrientationToggle'
import { useTableOrientationStore } from '../../store/useTableOrientationStore'
import { useHlyOneDayData } from '../../hooks/useHlyOneDayData'
import { wwLabel, visibilityLabel, wLabel, cldWindVal2TimeHLY } from './shared/weatherUtils'

const SLUG = 'hly-prehled'

export const HLY_COLUMNS = [
  {
    group: 'Teploty',
    subgroups: null,
    cols: [
      { tsId: 'HLY_TEMP',    label: 'Prům',     unit: '°C' },
      { tsId: 'HLY_TN',      label: 'Min.',      unit: '°C' },
      { tsId: 'HLY_TX',      label: 'Max.',      unit: '°C' },
      { tsId: 'HLY_TGROUND', label: 'Přízemní',  unit: '°C' },
      { tsId: 'HLY_DEWPOINT', label: 'Ros. bod',  unit: '°C' },
    ],
  },
  {
    group: 'Vítr',
    subgroups: [
      {
        label: 'Průměrný',
        cols: [
          { tsId: 'HLY_WSAVG', label: 'Rychlost', unit: 'm/s' },
          { tsId: 'HLY_WDAVG', label: 'Směr',     unit: '°'   },
        ],
      },
      {
        label: 'Maximální',
        cols: [
          { tsId: 'HLY_WSMAX', label: 'Rychlost', unit: 'm/s'   },
          { tsId: 'HLY_WDMAX', label: 'Směr',     unit: '°'     },
          { tsId: 'HLY_WTMAX', label: 'Čas max.', unit: 'HH:mm', formatCell: (v, time) => cldWindVal2TimeHLY(v, time) },
        ],
      },
    ],
    cols: null,
  },
  {
    group: 'Tlak',
    subgroups: null,
    cols: [
      { tsId: 'HLY_PRESS',    label: 'Stan.',      unit: 'hPa' },
      { tsId: 'HLY_PRESSSEA', label: 'Hl.moře',   unit: 'hPa' },
      { tsId: 'HLY_VAPPRESS', label: 'Vod. páry', unit: 'hPa' },
    ],
  },
  {
    group: 'Sluneční',
    subgroups: null,
    cols: [
      { tsId: 'HLY_SUNSHINE', label: 'Svit',         unit: '0.1 hod.' },
      { tsId: 'HLY_GLOBRAD',  label: 'Glob. záření', unit: 'W/m²' },
    ],
  },
 {
    group: 'Oblačnost',
    subgroups: null,
    cols: [
      { tsId: 'HLY_CLOUD', label: 'Oblačnost', unit: 'Osm.' },
    ],
  },  
{
    group: 'Viditelnost',
    subgroups: null,
    cols: [
      { tsId: 'HLY_VISIBILITY', label: 'Viditelnost', unit: 'km', formatCell: (v) => visibilityLabel(v) },
    ],
  },  
  {
    group: 'Srážky',
    subgroups: null,
    cols: [
      { tsId: 'HLY_PRECIP', label: 'Srážky', unit: 'mm' },
    ],
  },
  {
    group: 'Vlhkost',
    subgroups: null,
    cols: [
      { tsId: 'HLY_RAWHUMIDITY', label: 'Prům', unit: '%' },
    ],
  },
  {
    group: 'Půdní teploty',
    subgroups: null,
    cols: [
      { tsId: 'HLY_SOIL05',  label: '5 cm',   unit: '°C' },
      { tsId: 'HLY_SOIL10',  label: '10 cm',  unit: '°C' },
      { tsId: 'HLY_SOIL20',  label: '20 cm',  unit: '°C' },
      { tsId: 'HLY_SOIL50',  label: '50 cm',  unit: '°C' },
      { tsId: 'HLY_SOIL100', label: '100 cm', unit: '°C' },
    ],
  },
  {
    group: 'Stav počasí',
    subgroups: null,
    cols: [
      { tsId: 'HLY_ww',  label: 'ww',   unit: 'kód', titleCell: (v) => wwLabel(v) },
      { tsId: 'HLY_W1', label: 'W1', unit: 'kód', titleCell: (v) => wLabel(v), colSpan: (entry) => entry['HLY_W1'] != null && entry['HLY_W1'] === entry['HLY_W2'] ? 2 : 1 },
      { tsId: 'HLY_W2', label: 'W2', unit: 'kód', titleCell: (v) => wLabel(v) },
    ],
  },
]


// Transponovaná hlavička (řádky = veličiny). Popisy voleny delší – v transponované tabulce je
// pro ně místo. Skupina (row.group) se vykreslí jako oddělovací řádek hlavičky.
export const HLY_HEADER_TRANSPOSED = {
  groupLabel: 'Veličina',
  rows: [
    { tsId: 'HLY_TEMP',       group: 'Teploty',     label: 'Průměrná teplota',        unit: '°C' },
    { tsId: 'HLY_TN',         group: 'Teploty',     label: 'Minimální teplota',       unit: '°C' },
    { tsId: 'HLY_TX',         group: 'Teploty',     label: 'Maximální teplota',       unit: '°C' },
    { tsId: 'HLY_TGROUND',    group: 'Teploty',     label: 'Přízemní teplota',        unit: '°C' },
    { tsId: 'HLY_DEWPOINT',   group: 'Teploty',     label: 'Rosný bod',               unit: '°C' },
    { tsId: 'HLY_WSAVG',      group: 'Vítr',        label: 'Průměrná rychlost větru', unit: 'm/s' },
    { tsId: 'HLY_WDAVG',      group: 'Vítr',        label: 'Průměrný směr větru',     unit: '°' },
    { tsId: 'HLY_WSMAX',      group: 'Vítr',        label: 'Maximální rychlost větru', unit: 'm/s' },
    { tsId: 'HLY_WDMAX',      group: 'Vítr',        label: 'Směr maximálního větru',  unit: '°' },
    { tsId: 'HLY_WTMAX',      group: 'Vítr',        label: 'Čas maxima větru',        unit: 'HH:mm', formatCell: (v, time) => cldWindVal2TimeHLY(v, time) },
    { tsId: 'HLY_PRESS',      group: 'Tlak',        label: 'Tlak na stanici',         unit: 'hPa' },
    { tsId: 'HLY_PRESSSEA',   group: 'Tlak',        label: 'Tlak na hladině moře',    unit: 'hPa' },
    { tsId: 'HLY_VAPPRESS',   group: 'Tlak',        label: 'Tlak vodních par',        unit: 'hPa' },
    { tsId: 'HLY_SUNSHINE',   group: 'Sluneční',    label: 'Sluneční svit',           unit: '0.1 hod.' },
    { tsId: 'HLY_GLOBRAD',    group: 'Sluneční',    label: 'Globální záření',         unit: 'W/m²' },
    { tsId: 'HLY_CLOUD',      group: 'Oblačnost',   label: 'Oblačnost',               unit: 'Osm.' },
    { tsId: 'HLY_VISIBILITY', group: 'Viditelnost', label: 'Viditelnost',             unit: 'km', formatCell: (v) => visibilityLabel(v) },
    { tsId: 'HLY_PRECIP',     group: 'Srážky',      label: 'Srážky',                  unit: 'mm' },
    { tsId: 'HLY_RAWHUMIDITY', group: 'Vlhkost',    label: 'Relativní vlhkost',       unit: '%' },
    { tsId: 'HLY_SOIL05',     group: 'Půdní teploty', label: 'Půdní teplota 5 cm',    unit: '°C' },
    { tsId: 'HLY_SOIL10',     group: 'Půdní teploty', label: 'Půdní teplota 10 cm',   unit: '°C' },
    { tsId: 'HLY_SOIL20',     group: 'Půdní teploty', label: 'Půdní teplota 20 cm',   unit: '°C' },
    { tsId: 'HLY_SOIL50',     group: 'Půdní teploty', label: 'Půdní teplota 50 cm',   unit: '°C' },
    { tsId: 'HLY_SOIL100',    group: 'Půdní teploty', label: 'Půdní teplota 100 cm',  unit: '°C' },
    { tsId: 'HLY_ww',         group: 'Stav počasí', label: 'Stav počasí (ww)',        unit: 'kód', titleCell: (v) => wwLabel(v) },
    { tsId: 'HLY_W1',         group: 'Stav počasí', label: 'Průběh počasí W1',        unit: 'kód', titleCell: (v) => wLabel(v) },
    { tsId: 'HLY_W2',         group: 'Stav počasí', label: 'Průběh počasí W2',        unit: 'kód', titleCell: (v) => wLabel(v) },
  ],
}


export function HlyOneDayView({ station }) {
  const { date, setDate } = useDayPeriodStore()
  const orientation = useTableOrientationStore((s) => s.getOrientation(SLUG))

  const { dataMap, labelMap, dayMap, isLoading, isError } = useHlyOneDayData(station?.gh_id, date, HLY_COLUMNS)

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-white flex flex-col gap-3">
        {station && <StationPanelLinePhoto station={station} />}
        <DayPeriodPicker
          date={date}
          onChange={setDate}
          obsType="HLY"
          headerExtra={<TimeZonePicker trailing={<TableOrientationToggle slug={SLUG} />} />}
        />
      </div>

      {/* Grid area */}
      <div className="flex-1 min-h-0 p-4 flex flex-col">
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Spinner size="sm" /> Načítání dat…
          </div>
        )}
        {isError && (
          <p className="text-red-500 text-sm">Chyba při načítání dat</p>
        )}
        {!isLoading && !isError && (
          <MinGrid
            columns={HLY_COLUMNS}
            headerTransposed={HLY_HEADER_TRANSPOSED}
            orientation={orientation}
            dataMap={dataMap}
            labelMap={labelMap}
            dayMap={dayMap}
            rowGroupBorder={(_, label) => ['00:00', '06:00', '12:00', '18:00'].includes(label)}
          />
        )}
      </div>
    </div>
  )
}
